import { useEffect, useState } from "react";
import axios from "axios";
import SecureLS from "secure-ls";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import Swal from "sweetalert2";
import AlertsComms from "./Functions/AlertsComms";
import TextField from "@mui/material/TextField";
import dayjs from "dayjs";
import { useNavigate } from "react-router-dom";
import StoreTitle from "../../component/StoreTitle";
import LinearProgress from "@mui/material/LinearProgress";
import { Pie } from "react-chartjs-2";
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from "chart.js";

const formatSecondsToMMSS = (seconds) => {
  const mm = String(Math.floor(seconds / 60)).padStart(2, "0");
  const ss = String(seconds % 60).padStart(2, "0");
  return `${mm}:${ss}`;
};

const centerTextPlugin = {
  id: "centerTextPlugin",
  afterDraw(chart) {
    if (chart.config.type !== "pie" && chart.config.type !== "doughnut") return;

    const { width, height, ctx } = chart;
    const value = chart.config.data.datasets[0].data[0];
    const text = `${Math.round(value)}%`;

    ctx.save();
    const fontSize = Math.min(width, height) / 5;
    ctx.font = `bold ${fontSize}px Arial`;
    ctx.fillStyle = "#333";
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillText(text, width / 2, height / 2);
    ctx.restore();
  },
};

ChartJS.register(ArcElement, Tooltip, Legend, centerTextPlugin);

const GenericViewHero = ({
  title,
  ongoingCount = 0,
  averageOrderTime,
  stockAlerts,
  date,
  onDateChange,
  grossSales = 0,
  mode = "gross",
}) => {
  const navigate = useNavigate();
  const ls = new SecureLS({ encodingType: "aes" });
  const [toggles, setToggles] = useState({});
  const [goalAmount, setGoalAmount] = useState(null);
  const [showChart, setShowChart] = useState(false);

  const progressPercent =
    goalAmount && goalAmount > 0
      ? Math.min((grossSales / goalAmount) * 100, 100)
      : 0;

  useEffect(() => {
    const fetchTogglesAndGoal = async () => {
      try {
        const token = ls.get("authToken");
        const headers = { Authorization: `Bearer ${token}` };

        const [toggleRes, goalRes] = await Promise.all([
          axios.get(`${process.env.REACT_APP_BACKEND_URL}/v1/api/pos/toggles`, {
            headers,
          }),
          axios.get(
            `${
              process.env.REACT_APP_BACKEND_URL
            }/v1/api/pos/gross-sales-goal-kds?date=${dayjs(date).format(
              "YYYY-MM-DD"
            )}`,
            { headers }
          ),
        ]);

        setToggles(toggleRes.data);

        setGoalAmount(goalRes.data ?? null);
      } catch (err) {
        console.error("Error fetching toggles/goals:", err);
      }
    };

    if (date) fetchTogglesAndGoal();
  }, [date]);

  const pieData = {
    labels: ["Achieved", "Remaining"],
    datasets: [
      {
        data: [progressPercent, 100 - progressPercent],
        backgroundColor: ["#3f51b5", "#e0e0e0"],
        borderWidth: 1,
      },
    ],
  };

  const pieOptions = {
    responsive: true,
    plugins: {
      legend: { display: false },
      tooltip: {
        callbacks: {
          label: (context) => `${context.label}: ${context.parsed.toFixed(1)}%`,
        },
      },
      centerTextPlugin: true,
    },
    cutout: "70%",
  };

  return (
    <Box sx={{ px: 2, py: 1, mb: 2 }}>
      {/* ========== MOBILE VIEW ========== */}
      <Box sx={{ display: { xs: "block", sm: "none" } }}>
        <Button
          fullWidth
          variant="contained"
          color="primary"
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate("/views")}
          sx={{ mb: 2 }}
        >
          Back
        </Button>

        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 1,
          }}
        >
          <Button
            onClick={() => setShowChart(!showChart)}
            sx={{
              minWidth: 0,
              width: 40,
              height: 40,
              fontSize: "1.5rem",
              padding: 0,
            }}
          >
            ☰
          </Button>

          <TextField
            type="date"
            size="small"
            value={dayjs(date).format("YYYY-MM-DD")}
            onChange={(e) => onDateChange(e.target.value)}
            sx={{ minWidth: 150 }}
          />
        </Box>

        {showChart &&
          ((title.toLowerCase().includes("front of house") &&
            toggles["foh net sales"]) ||
            (title.toLowerCase().includes("kitchen view") &&
              toggles["boh net sales"])) && (
            <Box sx={{ textAlign: "center", mb: 1 }}>
              <Typography variant="caption">
                Goal for {dayjs(date).format("dddd")}
              </Typography>
              <Box sx={{ width: 100, height: 100, mx: "auto" }}>
                <Pie
                  data={pieData}
                  options={pieOptions}
                  plugins={[centerTextPlugin]}
                />
              </Box>
              <Typography variant="caption" sx={{ mt: 0.5 }}>
                ₱{grossSales.toLocaleString("en-PH")} / ₱
                {(goalAmount ?? 0).toLocaleString("en-PH")}
              </Typography>
            </Box>
          )}

        <Typography variant="h4" sx={{ textAlign: "center", mt: 1, mb: 1 }}>
          <StoreTitle title={title} />
        </Typography>

        <Box sx={{ textAlign: "right" }}>
          <Typography
            variant="subtitle1"
            sx={{ fontWeight: 600, fontSize: "1.2rem", display: "inline" }}
          >
            Ongoing Orders:
          </Typography>
          <Typography
            variant="h3"
            sx={{ fontWeight: "bold", display: "inline", ml: 1 }}
          >
            {ongoingCount || 0}
          </Typography>

          {typeof averageOrderTime === "number" && averageOrderTime > 0 && (
            <Typography
              variant="body2"
              sx={{ fontSize: "0.85rem", mt: 0.5, color: "#888" }}
            >
              Order Serving Time:{" "}
              {`${String(Math.floor(averageOrderTime / 60)).padStart(
                2,
                "0"
              )}:${String(Math.floor(averageOrderTime % 60)).padStart(2, "0")}`}
            </Typography>
          )}
        </Box>

        {stockAlerts && (
          <Box sx={{ mt: 2 }}>
            <AlertsComms stockAlerts={stockAlerts} />
          </Box>
        )}
      </Box>

      {/* ========== DESKTOP VIEW ========== */}
      <Box
        sx={{
          display: { xs: "none", sm: "flex" },
          alignItems: "center",
          justifyContent: "space-between",
          px: 1,
          py: 1,
          position: "relative",
          minHeight: 64,
          mt: -1.5,
        }}
      >
        {/* LEFT SECTION */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <Button
            variant="contained"
            color="primary"
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate("/views")}
          >
            Back
          </Button>

          <TextField
            type="date"
            size="small"
            value={dayjs(date).format("YYYY-MM-DD")}
            onChange={(e) => onDateChange(e.target.value)}
            sx={{ minWidth: 150 }}
          />

          {((title.toLowerCase().includes("front of house") &&
            toggles["foh net sales"]) ||
            (title.toLowerCase().includes("kitchen view") &&
              toggles["boh net sales"])) && (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
              }}
            >
              <Typography variant="caption">
                {mode === "net" ? "Net Goal" : "Gross Goal"} for{" "}
                {dayjs(date).format("dddd")}
              </Typography>
              <Box sx={{ width: 80, height: 80 }}>
                <Pie data={pieData} options={pieOptions} />
              </Box>
              <Typography variant="caption" sx={{ mt: 0.5 }}>
                ₱
                {grossSales.toLocaleString("en-PH", {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}{" "}
                / ₱
                {(goalAmount ?? 0).toLocaleString("en-PH", {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}
              </Typography>
            </Box>
          )}
        </Box>

        {/* CENTER TITLE */}
        <Typography
          variant="h4"
          sx={{
            position: "absolute",
            left: "50%",
            transform: "translateX(-50%)",
          }}
        >
          <StoreTitle title={title} />
        </Typography>

        {/* RIGHT SECTION */}
        <Typography component="div" sx={{ textAlign: "right" }}>
          <Typography
            variant="subtitle1"
            sx={{ fontWeight: 600, fontSize: "1.2rem", display: "inline" }}
          >
            Ongoing Orders:
          </Typography>
          <Typography
            variant="h3"
            sx={{ fontWeight: "bold", display: "inline", ml: 1 }}
          >
            {ongoingCount || 0}
          </Typography>

          {typeof averageOrderTime === "number" && averageOrderTime > 0 && (
            <Typography
              variant="body2"
              sx={{ fontSize: "0.85rem", mt: 0.5, color: "#888" }}
            >
              Order Serving Time:{" "}
              {`${String(Math.floor(averageOrderTime / 60)).padStart(
                2,
                "0"
              )}:${String(Math.floor(averageOrderTime % 60)).padStart(2, "0")}`}
            </Typography>
          )}
        </Typography>

        {/* ALERTS */}
        {stockAlerts && (
          <Box
            sx={{
              position: "absolute",
              textAlign: "right",
              bottom: -70,
              right: 16,
              zIndex: 10,
              maxHeight: 80,
              overflow: "hidden",
            }}
          >
            <AlertsComms stockAlerts={stockAlerts} />
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default GenericViewHero;
